import { Filters, ORDER_VALUE } from '@spo/logic';
import {
  PatchApiSpoSmBodySmMassEditItem,
  SpoPersonalOfferTableResponseDataItem,
  SpoUpdateSmResponse,
} from '@spo/types';
import { VpLoader } from '@vtmn-play/react';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePersonalOfferTable } from '../../hooks/usePersonalOfferTable';
import { useSuperModelsMassEdit } from '../../hooks/useSuperModelsMassEdit';
import { useUserConfig } from '../../hooks/useUserConfig';
import { columnsMassEdit } from '../../utils/columns/columns';
import { Footer } from '../footer';
import { MassEditInputs } from '../massEditInputs';
import { TableView } from '../table/modelsTable/tableView';
import { ModalErrorType } from './MassEdit';
import { MassEditErrorModal } from './MassEditErrorModal';

type SmMassEditProps = {
  selectedFilters: Filters;
  smIds: string[];
};

export const SmMassEdit = ({ selectedFilters, smIds }: SmMassEditProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [startDateSelected, setStartDateSelected] = useState<Date[]>([]);
  const [endDateSelected, setEndDateSelected] = useState<Date[]>([]);
  const [smDateMassEdit, setSmDateMassEdit] = useState<
    PatchApiSpoSmBodySmMassEditItem[]
  >([]);
  const [smMassEditErrorResponse, setSmMassEditErrorResponse] = useState<
    SpoUpdateSmResponse | undefined
  >([]);

  const [disabledStartDateInput, setDisabledStartDateInput] = useState(false);

  const { data: userConfigData } = useUserConfig();

  const {
    data: smMassEditResponse,
    isError: isErrorSmMassEdit,
    isLoading: isLoadingSMMassEdit,
    refetch,
  } = useSuperModelsMassEdit({ smMassEdit: smDateMassEdit });

  useEffect(() => {
    const smMassEditError = smMassEditResponse?.filter(
      (sm) => sm.status !== 200,
    );

    if (!smMassEditError?.length && smMassEditResponse) {
      navigate('/personal-offer/my-view', { state: { displaySnackBar: true } });
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSmMassEditErrorResponse(smMassEditError);
    }
  }, [smMassEditResponse]);

  const isValidateActionDisabled =
    !startDateSelected.length && !endDateSelected.length;

  const { data: superModelsTable } = usePersonalOfferTable(
    userConfigData,
    { start: 0, end: 99 },
    {
      id: 'startDate',
      order: ORDER_VALUE.DESC,
    },
    selectedFilters,
    smIds,
  );

  useEffect(() => {
    if (superModelsTable) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisabledStartDateInput(
        superModelsTable.data.filter(
          (sm) => sm.startDate > format(new Date(), 'yyyy-MM-dd'),
        ).length === 0,
      );
    }
  }, [superModelsTable]);

  const cancelSmMassEdit = () => {
    navigate(`/personal-offer/my-view?smIds=${smIds}`);
  };

  const validateSmMassEdit = () => {
    if (superModelsTable) {
      const smEdited: PatchApiSpoSmBodySmMassEditItem[] =
        superModelsTable.data.map((sm) => {
          return {
            id: sm.id,
            ...(startDateSelected.length > 0 && {
              startDate: format(startDateSelected[0], 'yyyy-MM-dd'),
            }),
            ...(endDateSelected.length > 0 && {
              endDate: format(endDateSelected[0], 'yyyy-MM-dd'),
            }),
          };
        });
      setSmDateMassEdit(smEdited);
    }
  };

  const isMassEditRequestInError = useMemo(
    () => smMassEditErrorResponse || isErrorSmMassEdit,
    [smMassEditErrorResponse, isErrorSmMassEdit],
  );

  return (
    <>
      <div data-testid="sm-mass-edit" className="sm-mass-edit">
        <MassEditInputs
          startDateSelected={startDateSelected}
          endDateSelected={endDateSelected}
          setStartDateSelected={setStartDateSelected}
          setEndDateSelected={setEndDateSelected}
          disabledStartDateInput={disabledStartDateInput}
        />
        {superModelsTable ? (
          <div className="container_mass-edit_table">
            <TableView<SpoPersonalOfferTableResponseDataItem>
              data={superModelsTable?.data}
              columns={columnsMassEdit()}
              massEdit
            />
          </div>
        ) : (
          <div
            data-testid="sm-mass-edit-table-loading"
            className="loading-container-sm-mass-edit"
          >
            <VpLoader size="medium" className="loading-sm-mass-edit" />
          </div>
        )}
        {isMassEditRequestInError && (
          <MassEditErrorModal
            errorResponse={smMassEditErrorResponse || []}
            type={ModalErrorType.SM}
            refetch={refetch}
          />
        )}
      </div>
      <Footer
        ctaLabel={t('footer.save-and-quit-button')}
        validateAction={validateSmMassEdit}
        cancelAction={cancelSmMassEdit}
        isValidateActionDisabled={isValidateActionDisabled}
        isLoading={isLoadingSMMassEdit}
      />
    </>
  );
};
